/**
 * ==========================================================================
 *  PflegeCockpit-MailCenter — Etappe 3 der Kommunikationszentrale
 * ==========================================================================
 *  Globaler Posteingang fuer das Berater-Dashboard.
 *
 *  Linke Spalte: Liste aller Mails (Filter ungelesen / nicht-zugeordnet,
 *  Suche, Pagination). Rote Pulse-Badges bei ungelesenen.
 *
 *  Rechte Spalte: Detail-View mit Header (Absender, Datum, Kunden-Bezug),
 *  Body, Aktionen (Zuordnen/Loesen, Antworten, Loeschen).
 *
 *  Antworten-Form geht direkt aus dem Detail raus — die Mail wird per
 *  POST /api/kunden/{kunde_id}/mails verschickt, mit in_reply_to fuer
 *  Threading. Bei nicht zugeordneten Mails ist Antworten erst moeglich,
 *  nachdem der Berater einem Kunden zugewiesen hat (oder die Zuordnung
 *  vorbei das Pflegecockpit-Tab umgeht — kommt spaeter).
 * ==========================================================================
 */
import React, { useState, useEffect, useRef } from 'react';
import { Mail, Search, Inbox, AlertCircle, Loader2, RefreshCw, X, Send, Trash2, UserPlus, UserMinus, ChevronLeft, ShieldOff } from 'lucide-react';
import { api, ApiError } from '../api';

const CI = { brombeer: '#85366C' };

export default function MailCenter({ onOpenKundeAkte } = {}) {
  const [items, setItems] = useState([]);
  const [gesamt, setGesamt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fehler, setFehler] = useState(null);

  const [filter, setFilter] = useState('alle'); // 'alle' | 'ungelesen' | 'nicht_zugeordnet'
  const [suche, setSuche] = useState('');
  const [seite, setSeite] = useState(0);
  const seiteGroesse = 30;

  const [selectedId, setSelectedId] = useState(null);
  // Lösch-/Block-Modal: hält die Mail, fuer die der Trash-Klick passiert ist.
  const [loeschKandidat, setLoeschKandidat] = useState(null);

  const ladeListe = async () => {
    setLoading(true); setFehler(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(seiteGroesse));
      params.set('offset', String(seite * seiteGroesse));
      if (filter === 'ungelesen') params.set('ungelesen', 'true');
      if (filter === 'nicht_zugeordnet') params.set('nicht_zugeordnet', 'true');
      if (suche.trim()) params.set('suche', suche.trim());
      const data = await api.get(`/api/kundenmail?${params.toString()}`);
      setItems(data?.items || []);
      setGesamt(data?.gesamt || 0);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung fehlgeschlagen.';
      setFehler(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { ladeListe(); /* eslint-disable-next-line */ }, [filter, seite]);

  const onSucheKey = (e) => {
    if (e.key === 'Enter') {
      setSeite(0);
      ladeListe();
    }
  };

  const handleAfterDetailAction = async () => {
    // Wenn rechts etwas geaendert wurde (Mail beantwortet / zugeordnet /
    // geloescht), muss die linke Liste frisch.
    await ladeListe();
  };

  const seitenAnzahl = Math.max(1, Math.ceil(gesamt / seiteGroesse));

  return (
    <div className="min-h-[calc(100vh-60px)] bg-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Mail className="w-6 h-6" style={{ color: CI.brombeer }} />
          <h1 className="text-2xl font-bold text-gray-900">Mail-Center</h1>
          <p className="text-sm text-gray-500 ml-2">
            Eingehende Kunden-Mails an hd@spa-pflegeberatung.de + ausgehende Cockpit-Mails.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
          {/* === Linke Spalte: Liste === */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <select
                value={filter}
                onChange={e => { setFilter(e.target.value); setSeite(0); }}
                className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
              >
                <option value="alle">Alle Mails</option>
                <option value="ungelesen">Nur ungelesene</option>
                <option value="nicht_zugeordnet">Nur nicht zugeordnet</option>
              </select>
              <button
                onClick={ladeListe}
                title="Liste neu laden"
                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <input
                type="text"
                value={suche}
                onChange={e => setSuche(e.target.value)}
                onKeyDown={onSucheKey}
                placeholder="In Betreff / Body / Absender suchen + Enter"
                className="w-full pl-8 pr-2 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {loading && (
              <div className="text-xs text-gray-400 italic flex items-center gap-2 px-1 py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lade...
              </div>
            )}
            {fehler && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mb-2">{fehler}</div>
            )}
            {!loading && items.length === 0 && (
              <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg px-4 py-6 text-center">
                <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                Keine Mails fuer diesen Filter.
              </div>
            )}

            <ul className="divide-y divide-gray-100 -mx-2">
              {items.map(it => (
                <MailListenZeile
                  key={it.id}
                  item={it}
                  selected={selectedId === it.id}
                  onSelect={() => setSelectedId(it.id)}
                  onLoeschen={() => setLoeschKandidat(it)}
                  onOpenKunde={() => {
                    if (it.kunde_id && onOpenKundeAkte) onOpenKundeAkte(it.kunde_id, 'mail');
                  }}
                  formatDatum={formatDatum}
                />
              ))}
            </ul>

            {gesamt > seiteGroesse && (
              <div className="flex items-center justify-between mt-3 px-1">
                <button
                  onClick={() => setSeite(s => Math.max(0, s - 1))}
                  disabled={seite === 0}
                  className="text-xs px-2 py-1 rounded border border-gray-300 disabled:opacity-40"
                >
                  ← zurueck
                </button>
                <span className="text-xs text-gray-500">
                  Seite {seite + 1} / {seitenAnzahl} · {gesamt} insgesamt
                </span>
                <button
                  onClick={() => setSeite(s => Math.min(seitenAnzahl - 1, s + 1))}
                  disabled={seite >= seitenAnzahl - 1}
                  className="text-xs px-2 py-1 rounded border border-gray-300 disabled:opacity-40"
                >
                  weiter →
                </button>
              </div>
            )}
          </div>

          {/* === Rechte Spalte: Detail === */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[60vh]">
            {!selectedId ? (
              <div className="text-center text-gray-400 italic py-20">
                <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                Eine Mail aus der Liste auswaehlen, um sie zu lesen oder zu beantworten.
              </div>
            ) : (
              <MailDetail
                mailId={selectedId}
                onClose={() => setSelectedId(null)}
                onChanged={handleAfterDetailAction}
              />
            )}
          </div>
        </div>
      </div>

      {loeschKandidat && (
        <LoeschBlockModal
          mail={loeschKandidat}
          onClose={() => setLoeschKandidat(null)}
          onDone={async () => {
            if (selectedId === loeschKandidat.id) setSelectedId(null);
            setLoeschKandidat(null);
            await ladeListe();
          }}
        />
      )}
    </div>
  );
}

// ==========================================================================
// LoeschBlockModal — Drei-Wege-Aktion beim Trash-Klick:
//   1. Nur diese Mail loeschen (Soft-Delete einer Zeile)
//   2. + Absender blockieren (alle kuenftigen Mails dieser Adresse)
//   3. + Ganze Domain blockieren (alle kuenftigen Mails *@domain)
// ==========================================================================
function LoeschBlockModal({ mail, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [fehler, setFehler] = useState(null);

  const absender = (mail.absender_email || '').toLowerCase();
  const domain = absender.includes('@') ? absender.split('@')[1] : '';

  const ausfuehren = async (aktion) => {
    setLoading(true); setFehler(null);
    try {
      if (aktion === 'nur_loeschen') {
        await api.del(`/api/kundenmail/${encodeURIComponent(mail.id)}`);
      } else {
        await api.post(`/api/kundenmail/${encodeURIComponent(mail.id)}/blockieren`, {
          typ: aktion === 'block_email' ? 'email' : 'domain',
        });
      }
      onDone();
    } catch (err) {
      setFehler(err?.detail || err?.message || 'Aktion fehlgeschlagen.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[80] p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            Mail loeschen
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Wie soll mit Mails von <span className="font-bold">{absender}</span> umgegangen werden?
          Die Original-Mail im Mitwald-Postfach bleibt in jedem Fall erhalten.
        </p>

        <div className="space-y-2">
          <button
            onClick={() => ausfuehren('nur_loeschen')}
            disabled={loading}
            className="w-full text-left px-4 py-3 border border-gray-300 hover:border-fuchsia-400 hover:bg-fuchsia-50 rounded-lg transition-all disabled:opacity-50"
          >
            <div className="flex items-center gap-2 mb-1">
              <Trash2 className="w-4 h-4 text-gray-500" />
              <span className="font-bold text-sm">Nur diese Mail loeschen</span>
            </div>
            <p className="text-xs text-gray-500 ml-6">
              Die naechste Mail vom selben Absender erscheint wieder im Cockpit.
            </p>
          </button>

          <button
            onClick={() => ausfuehren('block_email')}
            disabled={loading}
            className="w-full text-left px-4 py-3 border border-gray-300 hover:border-fuchsia-400 hover:bg-fuchsia-50 rounded-lg transition-all disabled:opacity-50"
          >
            <div className="flex items-center gap-2 mb-1">
              <ShieldOff className="w-4 h-4 text-amber-600" />
              <span className="font-bold text-sm">Absender dauerhaft blockieren</span>
            </div>
            <p className="text-xs text-gray-500 ml-6">
              Alle aktuellen + zukuenftigen Mails von <span className="font-mono">{absender}</span> verschwinden aus dem Cockpit.
            </p>
          </button>

          {domain && (
            <button
              onClick={() => ausfuehren('block_domain')}
              disabled={loading}
              className="w-full text-left px-4 py-3 border border-gray-300 hover:border-fuchsia-400 hover:bg-fuchsia-50 rounded-lg transition-all disabled:opacity-50"
            >
              <div className="flex items-center gap-2 mb-1">
                <ShieldOff className="w-4 h-4 text-red-600" />
                <span className="font-bold text-sm">Ganze Domain blockieren</span>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                Alle Adressen <span className="font-mono">@{domain}</span> verschwinden aus dem Cockpit (z.B. info@, news@, support@).
              </p>
            </button>
          )}
        </div>

        {fehler && (
          <div className="mt-3 text-xs bg-red-50 border border-red-200 rounded px-3 py-2 text-red-700">{fehler}</div>
        )}

        <div className="flex justify-end mt-4">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// MailDetail — Detail-View einer einzelnen Mail
// ==========================================================================
function MailDetail({ mailId, onClose, onChanged }) {
  const [mail, setMail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState(null);

  // Zuordnungs-Suche — laedt alle Kunden einmalig und filtert clientseitig.
  const [zuordnungSuche, setZuordnungSuche] = useState('');
  const [zuordnungOffen, setZuordnungOffen] = useState(false);
  const [alleKunden, setAlleKunden] = useState([]);
  const [kundenGeladen, setKundenGeladen] = useState(false);

  // Antwort-Form
  const [antwortOffen, setAntwortOffen] = useState(false);
  const [antwortText, setAntwortText] = useState('');
  const [antwortBetreff, setAntwortBetreff] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendeFehler, setSendeFehler] = useState(null);

  const ladeMail = async () => {
    setLoading(true); setFehler(null);
    try {
      const m = await api.get(`/api/kundenmail/${encodeURIComponent(mailId)}`);
      setMail(m);
      // Auto-Mark-Read fuer eingehende ungelesene
      if (m.richtung === 'eingehend' && !m.gelesen_am) {
        try {
          await api.post(`/api/kundenmail/${encodeURIComponent(mailId)}/gelesen`);
          if (onChanged) onChanged();
        } catch (e) {
          console.warn('Mail gelesen markieren fehlgeschlagen:', e);
        }
      }
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung fehlgeschlagen.';
      setFehler(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    ladeMail();
    setAntwortOffen(false);
    setAntwortText('');
    setSendeFehler(null);
    setZuordnungOffen(false);
    setZuordnungSuche('');
    // eslint-disable-next-line
  }, [mailId]);

  const ladeAlleKunden = async () => {
    if (kundenGeladen) return;
    try {
      const liste = await api.get('/api/kunden');
      setAlleKunden(liste || []);
      setKundenGeladen(true);
    } catch (e) {
      console.warn('Kundenliste konnte nicht geladen werden:', e);
    }
  };

  const zuordnungTreffer = (() => {
    const q = zuordnungSuche.trim().toLowerCase();
    if (!q || !alleKunden.length) return [];
    return alleKunden
      .filter(k => {
        const v = (k.firstName || '').toLowerCase();
        const n = (k.lastName || '').toLowerCase();
        const e = (k.email || '').toLowerCase();
        return v.includes(q) || n.includes(q) || e.includes(q) || `${v} ${n}`.includes(q);
      })
      .slice(0, 10);
  })();

  const handleZuordnen = async (kundeId) => {
    try {
      await api.put(`/api/kundenmail/${encodeURIComponent(mailId)}/zuordnen`, { kunde_id: kundeId });
      setZuordnungOffen(false);
      setZuordnungSuche('');
      await ladeMail();
      if (onChanged) onChanged();
    } catch (err) {
      alert('Zuordnung fehlgeschlagen: ' + (err?.detail || err?.message || 'unbekannt'));
    }
  };

  const handleZuordnungLoesen = async () => {
    if (!confirm('Zuordnung zu diesem Kunden wirklich loesen? Die Mail landet wieder im "Nicht zugeordnet"-Eingang.')) return;
    try {
      await api.put(`/api/kundenmail/${encodeURIComponent(mailId)}/zuordnen`, { kunde_id: null });
      await ladeMail();
      if (onChanged) onChanged();
    } catch (err) {
      alert('Loesen fehlgeschlagen: ' + (err?.detail || err?.message || 'unbekannt'));
    }
  };

  const handleLoeschen = async () => {
    if (!confirm('Mail wirklich aus dem Cockpit loeschen? Die Original-Mail im Mitwald-Postfach bleibt erhalten.')) return;
    try {
      await api.del(`/api/kundenmail/${encodeURIComponent(mailId)}`);
      onClose();
      if (onChanged) onChanged();
    } catch (err) {
      alert('Loeschen fehlgeschlagen: ' + (err?.detail || err?.message || 'unbekannt'));
    }
  };

  const handleAntwortenOeffnen = () => {
    if (!mail) return;
    const orig = mail.betreff || '';
    setAntwortBetreff(orig.toLowerCase().startsWith('re:') ? orig : `Re: ${orig}`);
    setAntwortText('');
    setSendeFehler(null);
    setAntwortOffen(true);
  };

  const handleAntwortSenden = async () => {
    if (!mail || !mail.kunde_id) {
      setSendeFehler('Diese Mail muss erst einem Kunden zugewiesen werden, bevor man antworten kann.');
      return;
    }
    if (!antwortBetreff.trim() || !antwortText.trim()) {
      setSendeFehler('Betreff und Text sind Pflicht.');
      return;
    }
    setIsSending(true); setSendeFehler(null);
    try {
      const fd = new FormData();
      fd.append('empfaenger_emails', mail.absender_email);
      fd.append('betreff', antwortBetreff.trim());
      fd.append('body_text', antwortText.trim());
      if (mail.message_id) fd.append('in_reply_to', mail.message_id);
      await api.postForm(`/api/kunden/${encodeURIComponent(mail.kunde_id)}/mails`, fd);
      setAntwortOffen(false);
      setAntwortText('');
      await ladeMail();
      if (onChanged) onChanged();
    } catch (err) {
      setSendeFehler(err instanceof ApiError ? (err.detail || `Fehler ${err.status}`) : 'Verbindung fehlgeschlagen.');
    } finally {
      setIsSending(false);
    }
  };

  if (loading) {
    return <div className="text-gray-400 italic flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lade...</div>;
  }
  if (fehler) {
    return <div className="text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{fehler}</div>;
  }
  if (!mail) return null;

  const eingehend = mail.richtung === 'eingehend';

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <button
          onClick={onClose}
          className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> zurueck zur Liste
        </button>
        <button
          onClick={handleLoeschen}
          className="text-xs text-red-600 hover:underline flex items-center gap-1"
          title="Mail aus dem Cockpit entfernen"
        >
          <Trash2 className="w-3.5 h-3.5" /> Loeschen
        </button>
      </div>

      <div className="border-b border-gray-200 pb-3">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h2 className="text-lg font-bold text-gray-900">{mail.betreff || '(ohne Betreff)'}</h2>
          <span className={`px-2 py-0.5 rounded text-xs font-bold border flex-shrink-0 ${eingehend ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
            {eingehend ? 'Eingehend' : 'Ausgehend'}
          </span>
        </div>
        <div className="text-xs text-gray-600 space-y-0.5">
          <div><span className="font-bold">{eingehend ? 'Von:' : 'An:'}</span> {eingehend ? mail.absender_email : (mail.empfaenger_emails || []).join(', ')}</div>
          <div><span className="font-bold">Datum:</span> {formatDatum(mail.created_at)}</div>
        </div>
      </div>

      {/* Kunden-Zuordnung */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
        {mail.kunde_id ? (
          <div className="flex items-center justify-between gap-2 text-sm">
            <span>
              Zugeordnet zu: <span className="font-bold text-fuchsia-700">{mail.kunde_name || mail.kunde_id}</span>
            </span>
            <button
              onClick={handleZuordnungLoesen}
              className="text-xs text-gray-600 hover:text-red-600 flex items-center gap-1"
            >
              <UserMinus className="w-3.5 h-3.5" /> Zuordnung loesen
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between gap-2 text-sm mb-1">
              <span className="text-amber-800">⚠ Diese Mail ist noch keinem Kunden zugeordnet.</span>
              <button
                onClick={() => { setZuordnungOffen(o => !o); ladeAlleKunden(); }}
                className="text-xs hover:underline flex items-center gap-1"
                style={{ color: CI.brombeer }}
              >
                <UserPlus className="w-3.5 h-3.5" /> Kunden zuweisen
              </button>
            </div>
            {zuordnungOffen && (
              <div className="mt-2 bg-white border border-gray-300 rounded-lg p-2">
                <input
                  type="text"
                  autoFocus
                  value={zuordnungSuche}
                  onChange={e => setZuordnungSuche(e.target.value)}
                  placeholder="Kunde suchen (Vor- oder Nachname)..."
                  className="w-full p-2 text-sm border border-gray-300 rounded-md mb-2"
                />
                {zuordnungTreffer.length > 0 ? (
                  <ul className="divide-y divide-gray-100">
                    {zuordnungTreffer.map(k => (
                      <li key={k.id}>
                        <button
                          onClick={() => handleZuordnen(k.id)}
                          className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-50 rounded"
                        >
                          <span className="font-medium">{k.firstName} {k.lastName}</span>
                          {k.email && <span className="text-gray-500 text-xs"> · {k.email}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  zuordnungSuche.trim() && <div className="text-xs text-gray-400 italic px-2 py-1">Keine Treffer.</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="text-sm text-gray-800 whitespace-pre-wrap break-words bg-white">
        {mail.body_text}
      </div>

      {/* Antworten — nur wenn eingehend */}
      {eingehend && (
        <div className="border-t border-gray-200 pt-4">
          {!antwortOffen ? (
            <button
              onClick={handleAntwortenOeffnen}
              className="px-4 py-2 rounded-lg font-bold text-white shadow-md flex items-center gap-2"
              style={{ backgroundColor: CI.brombeer }}
            >
              <Send className="w-4 h-4" /> Antworten
            </button>
          ) : (
            <div className="space-y-3">
              <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: CI.brombeer }}>
                <Send className="w-4 h-4" /> Antwort an {mail.absender_email}
              </h3>
              {!mail.kunde_id && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800 flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Bitte zuerst einem Kunden zuweisen — sonst kann die Mail nicht gespeichert werden.
                </div>
              )}
              <input
                type="text"
                value={antwortBetreff}
                onChange={e => setAntwortBetreff(e.target.value)}
                placeholder="Betreff"
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
              />
              <textarea
                value={antwortText}
                onChange={e => setAntwortText(e.target.value)}
                rows={10}
                placeholder="Liebe..."
                className="w-full p-2 border border-gray-300 rounded-lg text-sm font-sans"
              />
              {sendeFehler && (
                <div className="text-xs bg-red-50 border border-red-200 rounded px-3 py-2 text-red-700">{sendeFehler}</div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setAntwortOffen(false); setAntwortText(''); }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                  disabled={isSending}
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleAntwortSenden}
                  disabled={isSending}
                  className="px-6 py-2 rounded-lg font-bold text-white shadow-md disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: CI.brombeer }}
                >
                  {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {isSending ? 'Sende...' : 'Senden'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// MailListenZeile — eine Zeile in der linken Mail-Liste mit Trash-Button
// und klickbarem Kundennamen.
// ==========================================================================
function MailListenZeile({ item, selected, onSelect, onLoeschen, onOpenKunde, formatDatum }) {
  return (
    <li className={`group ${selected ? 'bg-fuchsia-50' : ''}`}>
      <div className="flex items-stretch hover:bg-gray-50">
        <button
          onClick={onSelect}
          className="flex-1 text-left px-3 py-3 min-w-0"
        >
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="text-xs text-gray-500 flex items-center gap-2 min-w-0">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${item.richtung === 'eingehend' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                {item.richtung === 'eingehend' ? 'Eingang' : 'Ausgang'}
              </span>
              <span className="truncate">{item.absender_email}</span>
            </div>
            {item.ungelesen && (
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white bg-red-500 animate-pulse flex-shrink-0">
                neu
              </span>
            )}
          </div>
          <div className={`text-sm mb-0.5 truncate ${item.ungelesen ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
            {item.betreff}
          </div>
          <div className="text-xs text-gray-500 truncate">{item.vorschau}</div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[11px]">
              {item.kunde_name ? (
                <span
                  role="link"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onOpenKunde && onOpenKunde(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onOpenKunde && onOpenKunde(); } }}
                  className="text-fuchsia-700 font-medium hover:underline cursor-pointer"
                  title={`Zur Akte von ${item.kunde_name} springen (Mail-Verlauf-Tab)`}
                >
                  → {item.kunde_name}
                </span>
              ) : (
                <span className="text-amber-700">Nicht zugeordnet</span>
              )}
            </span>
            <span className="text-[11px] text-gray-400">{formatDatum(item.created_at)}</span>
          </div>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onLoeschen(); }}
          title="Mail aus dem Cockpit entfernen"
          className="px-3 text-gray-300 hover:text-red-600 hover:bg-red-50 transition-colors flex items-center opacity-0 group-hover:opacity-100"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}

function formatDatum(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
